import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { UserCircle, Search, Eye, Edit } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import useCustomFields from '../hooks/useCustomFields';
import CustomFieldCell from '../components/custom-fields/CustomFieldCell';
import DataTable from '../components/common/DataTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ViewCustomers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const { fields: listCustomFields } = useCustomFields('customer', { listViewOnly: true });

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const filteredCustomers = customers.filter(customer =>
    customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (customer.search_term && customer.search_term.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const columns = [
    { key: 'customer_id', header: 'Customer ID', filterable: true, accessor: (c) => c.customer_id,
      render: (c) => <span className="font-medium">{c.customer_id}</span> },
    { key: 'name', header: 'Name', filterable: true, accessor: (c) => c.name },
    { key: 'city', header: 'City', accessor: (c) => c.city },
    { key: 'country', header: 'Country', accessor: (c) => c.country },
    { key: 'account_group_id', header: 'Account Group',
      render: (c) => <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">{c.account_group_id}</span> },
    { key: 'company_codes', header: 'Company Codes', align: 'center', accessor: (c) => c.company_code_data?.length || 0 },
    { key: 'sales_areas', header: 'Sales Areas', align: 'center', accessor: (c) => c.sales_area_data?.length || 0 },
    ...listCustomFields.map((f) => ({
      key: `cf_${f.fieldname}`, header: f.label,
      render: (c) => (
        <span data-testid={`customer-cf-${f.fieldname}-${c.customer_id}`}>
          <CustomFieldCell field={f} value={c.custom_data?.[f.fieldname]} />
        </span>
      ),
    })),
    { key: 'actions', header: 'Actions', align: 'right',
      render: (c) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => navigate(`/customer-view/${c.customer_id}`)} className="text-orange-600 hover:text-orange-900">
            <Eye className="w-4 h-4 mr-1" />View
          </Button>
          <Button size="sm" variant="ghost" onClick={() => navigate(`/customer-edit/${c.customer_id}`)} className="text-green-600 hover:text-green-900">
            <Edit className="w-4 h-4 mr-1" />Edit
          </Button>
        </div>
      ) },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <UserCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">View Customers</h1>
                <p className="text-sm text-gray-600">Browse and manage customer records</p>
              </div>
            </div>
            <Button onClick={() => navigate('/customer-creation')} className="bg-orange-600 hover:bg-orange-700">
              Create New Customer
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search by Customer ID, Name, or Search Term..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Customers Table */}
        <Card className="p-6">
          <DataTable
            testid="customers-table"
            columns={columns}
            data={filteredCustomers}
            loading={loading}
            rowKey={(c) => c.customer_id}
            emptyText={searchTerm ? 'No customers found matching your search' : 'No customers found'}
            rowProps={(c) => ({ 'data-testid': `customer-${c.customer_id}` })}
          />
        </Card>
      </div>
    </Layout>
  );
};

export default ViewCustomers;
